import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";

const GOLD = "#C9A84C";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const today = () => new Date().toISOString().slice(0, 10);

export function useJourney(api, uid) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(uid ? `/admin/directors/${uid}/journey` : "/director/journey").then((r) => setData(r.data)).catch(() => {}), [api, uid]);
  useEffect(() => { load(); }, [load]);
  return { data, setData, reload: load };
}

export function JourneyTimeline({ data, admin, onMark, onClear, busyStage }) {
  const [editing, setEditing] = useState(null);
  const [when, setWhen] = useState(today());
  if (!data) return <div className="h-16 animate-pulse bg-white/5" />;
  return (
    <div data-testid="journey-timeline" className="mt-5 pt-5 border-t border-[#C9A961]/10">
      <div className="flex items-center justify-between mb-4">
        <div className="font-grotesk text-[9px] uppercase tracking-[0.34em]" style={{ color: GOLD }}>Onboarding journey · {data.completed}/{data.total || 7}</div>
        <div className="flex items-center gap-2">
          {data.vcb_access && <span data-testid="journey-vcb-badge" className="inline-flex items-center gap-1.5 border px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.3em]" style={{ borderColor: `${GOLD}88`, color: GOLD }}>VCB access</span>}
          {data.inducted && <span data-testid="journey-inducted-badge" className="inline-flex items-center gap-1.5 border px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.3em]" style={{ borderColor: GOLD, color: GOLD, background: `${GOLD}1a` }}>◆ Inducted</span>}
        </div>
      </div>
      <ol className="relative grid grid-cols-4 md:grid-cols-7 gap-y-6 gap-x-2">
        <div className="hidden md:block absolute left-[8%] right-[8%] top-[9px] h-px" style={{ background: `linear-gradient(90deg, ${GOLD}, ${GOLD}33)` }} />
        {data.stages.map((s) => (
          <li key={s.n} className="relative flex flex-col items-center text-center" data-testid={`journey-stage-${s.n}`}>
            <span className="relative z-10 h-[18px] w-[18px] rounded-full border-2 grid place-items-center" style={{ borderColor: GOLD, background: s.done ? GOLD : "#0A0A0A", boxShadow: s.done ? `0 0 14px ${GOLD}88` : "none" }}>
              {s.done && <Check size={10} className="text-black" strokeWidth={3} />}
            </span>
            <div className="mt-2 font-serif text-[12px] leading-tight" style={{ color: s.done ? "#F5F5F0" : "#8A8A82" }}>{s.title}</div>
            <div className="font-mono text-[9px] mt-1" style={{ color: s.done ? GOLD : "#4A4A44" }} data-testid={`journey-stage-${s.n}-date`}>{s.done ? fmt(s.done_at) : "Pending"}</div>
            {s.done && s.manual && <div className="text-[8px] uppercase tracking-[0.2em] text-[#8A8A82] mt-0.5">manual</div>}
            {admin && (
              <div className="mt-2 flex items-center gap-1">
                {editing === s.n ? (
                  <div className="flex flex-col items-center gap-1">
                    <input type="date" value={when} max={today()} onChange={(e) => setWhen(e.target.value)} data-testid={`journey-date-${s.n}`} className="bg-black/50 border border-white/15 text-[10px] text-[#F5F5F0] px-1 py-0.5 w-[118px]" />
                    <div className="flex gap-1">
                      <button data-testid={`journey-save-${s.n}`} disabled={busyStage === s.n} onClick={async () => { await onMark(s.n, new Date(when + "T12:00:00").toISOString()); setEditing(null); }}
                              className="px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] bg-[#C9A84C] text-black">{busyStage === s.n ? <Loader2 size={9} className="animate-spin" /> : "Save"}</button>
                      <button onClick={() => setEditing(null)} className="px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] border border-white/20 text-[#C9C9C2]">Esc</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <label className="inline-flex items-center gap-1 cursor-pointer text-[9px] uppercase tracking-[0.18em] text-[#C9C9C2]">
                      <input type="checkbox" checked={s.done} data-testid={`journey-tick-${s.n}`} onChange={() => (s.manual ? onClear(s.n) : (s.done ? toast.message("This stage was reached organically — nothing to clear.") : setEditing(s.n)))}
                             className="accent-[#C9A84C]" /> {s.manual ? "clear" : s.done ? (s.source === "manual_bank_transfer" ? "manual" : "auto") : "mark"}
                    </label>
                    {s.manual && <button title="Clear manual tick" data-testid={`journey-clear-${s.n}`} onClick={() => onClear(s.n)} className="text-[#C0392B]"><X size={10} /></button>}
                  </>
                )}
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function ManualFeeDialog({ api, director, open, onOpenChange, onSaved }) {
  const [f, setF] = useState({ confirmed: false, amount: director?.fee_amount || 250000, utr: "", received_on: today(), notes: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.confirmed) return toast.error("Tick the payment confirmation box first.");
    if (!f.utr.trim()) return toast.error("Enter the transfer reference / UTR number.");
    setBusy(true);
    try {
      const r = await api.post(`/admin/directors/${director.id}/fee/manual`, { ...f, amount: Number(f.amount), received_on: new Date(f.received_on + "T12:00:00").toISOString() });
      toast.success("Fee acknowledged · PAID — MANUAL BANK TRANSFER", { description: `UTR ${r.data.fee_manual.utr}` });
      onSaved?.(r.data); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const L = ({ k, l, type = "text" }) => (
    <label className="block"><span className="block font-grotesk text-[8px] uppercase tracking-[0.24em] text-[#8A8A82] mb-1">{l}</span>
      <Input type={type} data-testid={`manual-fee-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-9 bg-black/40 border-white/15 text-sm" /></label>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-[#C9A84C]/40 text-[#F5F5F0]" data-testid="manual-fee-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl" style={{ color: GOLD }}>Acknowledge offline fee</DialogTitle>
          <DialogDescription className="text-[#A9A9A2] text-[13px]">For NEFT / RTGS / bank transfers received outside the gateway. Marks {director?.name || director?.email}'s onboarding fee as <b className="text-[#F5F5F0]">PAID — MANUAL BANK TRANSFER</b> and fires the Stage 2 vault moment on their next login.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {L({ k: "amount", l: "Amount received (₹)", type: "number" })}
          {L({ k: "received_on", l: "Date payment received", type: "date" })}
          <div className="col-span-2">{L({ k: "utr", l: "Transfer reference / UTR number" })}</div>
          <label className="col-span-2 block"><span className="block font-grotesk text-[8px] uppercase tracking-[0.24em] text-[#8A8A82] mb-1">Admin remarks</span>
            <Textarea data-testid="manual-fee-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="bg-black/40 border-white/15 text-sm" /></label>
          <label className="col-span-2 inline-flex items-start gap-2 text-[12px] text-[#C9C9C2] cursor-pointer">
            <input type="checkbox" data-testid="manual-fee-confirm" checked={f.confirmed} onChange={(e) => setF({ ...f, confirmed: e.target.checked })} className="mt-0.5 accent-[#C9A84C]" />
            I confirm this payment has been received in Vinci Inc.'s bank account and the reference above matches the credit.
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/20 text-[#C9C9C2]">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="manual-fee-save" className="bg-[#C9A84C] text-black font-grotesk uppercase tracking-[0.24em] text-[10px] hover:bg-[#E0C070]">{busy ? <Loader2 size={12} className="animate-spin" /> : "Mark as paid"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
